import * as THREE from 'three';

export function mergeBufferGeometries(geometries: THREE.BufferGeometry[]): THREE.BufferGeometry {
  if (geometries.length === 0) {
    throw new Error('mergeBufferGeometries requires at least one geometry');
  }
  const positions: number[] = [];
  const normals: number[] = [];
  const uvs: number[] = [];
  const colors: number[] = [];
  const hasUv = geometries.every((geometry) => Boolean(geometry.getAttribute('uv')));
  const hasNormal = geometries.every((geometry) => Boolean(geometry.getAttribute('normal')));
  const hasColor = geometries.every((geometry) => Boolean(geometry.getAttribute('color')));

  for (const geometry of geometries) {
    const position = geometry.getAttribute('position');
    const normal = geometry.getAttribute('normal');
    const uv = geometry.getAttribute('uv');
    const color = geometry.getAttribute('color');
    const index = geometry.getIndex();
    const pushVertex = (vertexIndex: number) => {
      positions.push(position.getX(vertexIndex), position.getY(vertexIndex), position.getZ(vertexIndex));
      if (hasNormal && normal) {
        normals.push(normal.getX(vertexIndex), normal.getY(vertexIndex), normal.getZ(vertexIndex));
      }
      if (hasUv && uv) {
        uvs.push(uv.getX(vertexIndex), uv.getY(vertexIndex));
      }
      if (hasColor && color) {
        colors.push(color.getX(vertexIndex), color.getY(vertexIndex), color.getZ(vertexIndex));
      }
    };
    if (index) {
      for (let i = 0; i < index.count; i += 1) {
        pushVertex(index.getX(i));
      }
    } else {
      for (let i = 0; i < position.count; i += 1) {
        pushVertex(i);
      }
    }
  }

  const merged = new THREE.BufferGeometry();
  merged.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  if (hasNormal) {
    merged.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
  } else {
    merged.computeVertexNormals();
  }
  if (hasUv) {
    merged.setAttribute('uv', new THREE.Float32BufferAttribute(uvs, 2));
  }
  if (hasColor) {
    merged.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  }
  return merged;
}

function relativeMatrix(item: THREE.Object3D, parent?: THREE.Object3D): THREE.Matrix4 {
  item.updateWorldMatrix(true, false);
  if (!parent) return item.matrix.clone();
  parent.updateWorldMatrix(true, false);
  return new THREE.Matrix4().copy(parent.matrixWorld).invert().multiply(item.matrixWorld);
}

export function mergeMeshes(
  meshes: THREE.Mesh[],
  material: THREE.Material,
  parent?: THREE.Object3D,
  options: { cast?: boolean; receive?: boolean } = {},
): THREE.Mesh {
  const transformed = meshes.map((item) => {
    const cloned = item.geometry.clone();
    cloned.applyMatrix4(relativeMatrix(item, parent));
    return cloned;
  });
  const merged = mergeBufferGeometries(transformed);
  transformed.forEach((geometry) => geometry.dispose());
  const mesh = new THREE.Mesh(merged, material);
  mesh.castShadow = options.cast ?? true;
  mesh.receiveShadow = options.receive ?? true;
  return mesh;
}

/**
 * Flattens every mesh under `root` into one geometry whose `color` attribute carries each
 * part's material colour. Rendered with a single vertex-coloured material this turns a
 * multi-colour prop into one draw call (and one instanced prototype).
 */
export function bakeColored(root: THREE.Object3D): THREE.BufferGeometry {
  root.updateMatrixWorld(true);
  const pieces: THREE.BufferGeometry[] = [];
  const tint = new THREE.Color();
  root.traverse((child) => {
    if (!(child instanceof THREE.Mesh) || Array.isArray(child.material)) return;
    const cloned = child.geometry.clone();
    cloned.applyMatrix4(relativeMatrix(child, root));
    const material = child.material as THREE.MeshStandardMaterial;
    tint.copy(material.color);
    const count = cloned.getAttribute('position').count;
    const colors = new Float32Array(count * 3);
    for (let i = 0; i < count; i += 1) {
      colors[i * 3] = tint.r;
      colors[i * 3 + 1] = tint.g;
      colors[i * 3 + 2] = tint.b;
    }
    cloned.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    pieces.push(cloned);
  });
  if (pieces.length === 0) {
    throw new Error('bakeColored found no meshes');
  }
  const merged = mergeBufferGeometries(pieces);
  pieces.forEach((geometry) => geometry.dispose());
  return merged;
}

/** Writes one flat vertex colour onto a geometry so it can join a `mats.baked` merge. */
export function tintGeometry(geometry: THREE.BufferGeometry, color: THREE.Color | number): THREE.BufferGeometry {
  const tint = color instanceof THREE.Color ? color : new THREE.Color(color);
  const count = geometry.getAttribute('position').count;
  const colors = new Float32Array(count * 3);
  for (let i = 0; i < count; i += 1) {
    colors[i * 3] = tint.r;
    colors[i * 3 + 1] = tint.g;
    colors[i * 3 + 2] = tint.b;
  }
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
  return geometry;
}

/** Re-projects UVs from world XZ so tiled ground textures line up across merged pads. */
export function planarUv(geometry: THREE.BufferGeometry, unitsPerTile: number): THREE.BufferGeometry {
  const position = geometry.getAttribute('position');
  const uv = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i += 1) {
    uv[i * 2] = position.getX(i) / unitsPerTile;
    uv[i * 2 + 1] = position.getZ(i) / unitsPerTile;
  }
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  return geometry;
}

export type InstanceTransform = {
  position: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number] | number;
  color?: number;
};

const dummy = new THREE.Object3D();
const instanceColor = new THREE.Color();

export function createInstancedMesh(
  geometry: THREE.BufferGeometry,
  material: THREE.Material,
  transforms: InstanceTransform[],
  options: { cast?: boolean; receive?: boolean } = {},
): THREE.InstancedMesh {
  const mesh = new THREE.InstancedMesh(geometry, material, transforms.length);
  let useColors = false;
  transforms.forEach((transform, index) => {
    dummy.position.set(...transform.position);
    dummy.rotation.set(...(transform.rotation ?? [0, 0, 0]));
    const scale = transform.scale ?? 1;
    if (typeof scale === 'number') {
      dummy.scale.setScalar(scale);
    } else {
      dummy.scale.set(...scale);
    }
    dummy.updateMatrix();
    mesh.setMatrixAt(index, dummy.matrix);
    if (transform.color != null) {
      useColors = true;
      mesh.setColorAt(index, instanceColor.setHex(transform.color));
    }
  });
  mesh.instanceMatrix.needsUpdate = true;
  if (useColors && mesh.instanceColor) {
    mesh.instanceColor.needsUpdate = true;
  }
  mesh.castShadow = options.cast ?? true;
  mesh.receiveShadow = options.receive ?? true;
  return mesh;
}

export function bakeGroupGeometry(group: THREE.Group, material: THREE.Material): THREE.BufferGeometry {
  const pieces: THREE.BufferGeometry[] = [];
  group.updateMatrixWorld(true);
  group.traverse((child) => {
    if (!(child instanceof THREE.Mesh) || child.material !== material) {
      return;
    }
    const cloned = child.geometry.clone();
    cloned.applyMatrix4(relativeMatrix(child, group));
    pieces.push(cloned);
  });
  if (pieces.length === 0) {
    throw new Error('bakeGroupGeometry found no matching meshes');
  }
  const merged = mergeBufferGeometries(pieces);
  pieces.forEach((geometry) => geometry.dispose());
  return merged;
}
